import React, { useRef } from "react";
import { useField, useFormikContext } from "formik";
import { HtmlEditorFieldConfig } from "../types";
import FormGroup from "../formGroup";
import FormLabel from "../formLabel";
import ErrorField from "../errorField";
import { FieldResetWhenRemoved, getValueForReadOnlyFieldForStaticForm } from "../../util";
import TemplateEditor from "shared/form/core/editor";
import { ToolbarType } from "shared/form/core/editor/enum";
import { FormTextEditorSize } from "shared/form/enum";
import { useFormContextData } from "shared/form/context";
import FieldRenderType from "shared/form/renderer/enums/FieldRenderType";
import FieldValueCount from "shared/components/fieldValueCount";

function HtmlEditorField(props: HtmlEditorFieldConfig) {
  const formContextData = useFormContextData();
  const { setFieldTouched, setFieldValue } = useFormikContext();
  const valueRef = useRef("");
  const [{ value }, meta] = useField({
    name: props.name,
    validate: props.validate
  });
  valueRef.current = value;

  const changeHandler = React.useCallback(
    async value => {
      await setFieldValue(props.name, value);
      if (!meta.touched) {
        await setFieldTouched(props.name, true);
      }
    },
    [setFieldValue, setFieldTouched, meta.touched, props.name]
  );

  const blurHandler = React.useCallback(
    async e => {
      await setFieldValue(props.name, valueRef.current);
      if (!meta.touched) {
        await setFieldTouched(props.name, true);
      }
    },
    [setFieldValue, props.name, meta.touched, setFieldTouched]
  );

  return (
    <FormGroup className={props.className} fullWidth={props.fullWidth} indented={props.indented}>
      <FormLabel label={props.label} required={props.required} infoLabel={props.infoLabel} />
      {formContextData.isReadOnly ? (
        getValueForReadOnlyFieldForStaticForm({ renderType: FieldRenderType.MULTI_LINE_TEXTFIELD_HTML_INPUT, value })
      ) : (
        <TemplateEditor
          value={value}
          onBlur={blurHandler}
          handleEditorChange={changeHandler}
          disabled={props.disabled}
          toolbarType={props.toolbarType || ToolbarType.BASIC}
          height={props.height || FormTextEditorSize.DEFAULT_SIZE}
          removeInitialPTag={props.removeInitialPTag}
          placeholder={props.placeholder}
        />
      )}
      <div className="errorMessage-wrapper">
        <ErrorField meta={meta} />
        {props.withCount && !formContextData.isReadOnly && (
          <FieldValueCount maxCount={props.maxCount} value={value} showCharacterCount />
        )}
      </div>
      {props.enableFieldReset && (
        <FieldResetWhenRemoved name={props.name} fixedInitialValueOnReset={props.fixedInitialValueOnReset} />
      )}
    </FormGroup>
  );
}

export default HtmlEditorField;
